/**
 * The footer of a paged table: rows per page and the match count on the left
 * ("Rows per page: 50 · 132 ideas"), "Page 2 of 3" with Previous / Next on
 * the right.
 *
 * A keyset-paged list (the audit log) has no total: pass `shown` and
 * `hasNext` instead, and the footer reads "· 51–100 entries" and "Page 2".
 */

import { ChevronDown } from 'lucide-react';

const PAGE_SIZES = [25, 50, 100];

export function Pagination({
  page,
  pageSize,
  total,
  shown = 0,
  hasNext,
  noun,
  note,
  onPage,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  /** Every matching row; leave it out when the list cannot count them. */
  total?: number;
  /** Without a total: the rows on this page, and whether a next one exists. */
  shown?: number;
  hasNext?: boolean;
  /** Plural for the count, e.g. "ideas". */
  noun: string;
  /** A muted word beside the count, e.g. "Archived hidden". */
  note?: string;
  onPage: (page: number) => void;
  /** Leave it out when the server fixes the page size: the footer then states it. */
  onPageSize?: (pageSize: number) => void;
}) {
  const pages = total === undefined ? null : Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;

  return (
    <nav className="pagination" aria-label="Pages">
      <div className="pagination-side">
        {onPageSize ? (
          <label className="pagination-size">
            <span>Rows per page:</span>
            <span className="select-wrap">
              <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" />
            </span>
          </label>
        ) : (
          <span className="pagination-size">{pageSize} per page</span>
        )}
        <span className="pagination-count" aria-live="polite">
          · {pages === null ? `${first}–${first + shown - 1}` : total} {noun}
        </span>
        {note && <span className="pagination-note muted">· {note}</span>}
      </div>
      <div className="pagination-side">
        <span className="pagination-page">
          {pages === null ? `Page ${page}` : `Page ${Math.min(page, pages)} of ${pages}`}
        </span>
        <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={pages === null ? !hasNext : page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </nav>
  );
}
